import ReactModal from 'react-modal';
import { faTimes, faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import Tippy from '@tippyjs/react';

import { cn } from 'src/common/utils';
import { refreshViewportHeight } from 'src/common/utils/viewportHeight';
import { ActionIcon } from 'src/common/components/buttons/ActionIcon';

const Modal = ({
  isOpen,
  onRequestClose,
  className,
  overlayClassName,
  children,
  ...rest
}: ReactModal.Props) => {
  useEffect(() => {
    if (!isOpen) {
      refreshViewportHeight();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <ReactModal
      isOpen
      onRequestClose={onRequestClose}
      onAfterOpen={() => {
        const elements = document.getElementsByClassName('ReactModal__Overlay');
        const overlay = elements[elements.length - 1];
        overlay.scrollTop = 0;
      }}
      className={cn(
        'anchor relative rounded bg-white outline-none',
        className?.toString(),
      )}
      overlayClassName={cn(
        'items-start bg-black/50 justify-center left-0 overflow-y-auto fixed top-0 w-full flex anchor h-[100dvh] min-h-[100dvh] p-2',
        overlayClassName?.toString(),
      )}
      {...rest}
    >
      {children}
    </ReactModal>
  );
};

const Header = ({
  children,
  onClose,
  helpContent,
  helpTitle,
  className,
}: {
  children: React.ReactNode;
  onClose?: () => void;
  helpContent?: string;
  helpTitle?: string;
  className?: string;
}) => {
  return (
    <div
      className={cn('flex items-center justify-between px-6 pt-6', className)}
    >
      <div className="flex items-center gap-2">
        <h1 className="text-xl font-semibold">{children}</h1>
        {helpContent && (
          <Tippy
            content={
              <div className="max-w-sm rounded border border-gray-300 bg-white p-4 shadow-lg">
                {helpTitle && (
                  <div className="mb-3 text-base font-semibold text-gray-900">
                    {helpTitle}
                  </div>
                )}
                <div
                  dangerouslySetInnerHTML={{ __html: helpContent }}
                  className="text-sm leading-relaxed text-gray-700"
                />
              </div>
            }
            allowHTML={true}
            interactive={true}
            trigger="click"
            placement="bottom-start"
            maxWidth={400}
            arrow={true}
            appendTo={document.body}
          >
            <ActionIcon size="md" className="text-blue-600 hover:text-blue-800">
              <FontAwesomeIcon icon={faQuestionCircle} size="lg" />
            </ActionIcon>
          </Tippy>
        )}
      </div>
      {onClose && (
        <ActionIcon onClick={onClose} className="ml-4">
          <FontAwesomeIcon icon={faTimes} />
        </ActionIcon>
      )}
    </div>
  );
};

const Body = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <div className={cn('p-6', className)}>{children}</div>;
};

const Title = ({ children }: { children: React.ReactNode }) => {
  return <h1 className="text-xl font-semibold">{children}</h1>;
};

const Close = ({ onClose }: { onClose: () => void }) => {
  return (
    <ActionIcon onClick={onClose} className="ml-4">
      <FontAwesomeIcon icon={faTimes} />
    </ActionIcon>
  );
};

Modal.Header = Header;
Modal.Body = Body;
Modal.Title = Title;
Modal.Close = Close;

export { Modal };

export function useModal() {
  const [isOpen, setIsOpen] = useState(false);

  function open() {
    setIsOpen(true);
  }

  function close() {
    setIsOpen(false);
  }

  function toggle() {
    setIsOpen(!isOpen);
  }

  return { isOpen, open, close, toggle };
}
